// src/layouts/components/AppSidebar.jsx

import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/authContext";
import { appRegistry } from "../../data/appRegistry.js";
import AVIcon from "../../components/icons/AllVoicedIcons";
import {
  GLOBAL_USER_ROLE_OPTIONS
} from "../../constants/globalUserRoles";

function getRouteFromItem(item) {
  return item.route || `/app/${item.id}`;
}

export default function AppSidebar({
  sidebarOpen,
  isDesktopViewport,
  setSidebarOpen,
  t,
  effectiveRole,
  setEffectiveRole,
  onOpenHelpSupport,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const userId = user?._id || null;
  const userRole = user?.role || "standard_user";
  const roleToCheck = effectiveRole || userRole;

  const sidebarItems = appRegistry
  .filter((item) => item.showInSidebar)
  .sort((a, b) => {
    const orderA = a.sidebarOrder ?? 999;
    const orderB = b.sidebarOrder ?? 999;

    return orderA - orderB;
  });

  const canAccess = (entry) => {
    if (!entry) {
      return false;
    }

    const roleAllowed =
      !entry.allowedRoles ||
      entry.allowedRoles.includes(roleToCheck);

    const userAllowed =
      !entry.allowedUsers ||
      entry.allowedUsers.includes(userId);

    return roleAllowed && userAllowed;
  };

  const isActive = (item) => {
    const route = getRouteFromItem(item);

    if (route === "/app") {
      return location.pathname === "/app";
    }

    return (
      location.pathname === route ||
      location.pathname.startsWith(`${route}/`)
    );
  };

  const renderNavButton = (item, options = {}) => {
    if (!canAccess(item)) {
      return null;
    }

    const isPrimary =
      item.variant === "primary" ||
      options.isPrimary;

    return (
      <button
        key={item.id}
        className={[
          "sidebar-btn",
          isPrimary ? "sidebar-btn--primary" : "",
          isActive(item) ? "active" : ""
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => {
          navigate(getRouteFromItem(item));
          setSidebarOpen?.(false);
        }}
        type="button"
      >
        <AVIcon
          icon={item.icon || "isotypePositive"}
          size={22}
          className="sidebar-icon"
          showAlert={
            item.id === "profile" &&
            user?.isVerified !== true
          }
        />

        <span>{t(item.labelKey)}</span>
      </button>
    );
  };

  return (
    <aside
      aria-hidden={!isDesktopViewport && !sidebarOpen ? "true" : undefined}
      aria-label="All-Voiced application navigation"
      className={`app-sidebar ${sidebarOpen ? "open" : "closed"}`}
      inert={!isDesktopViewport && !sidebarOpen ? "" : undefined}
    >
      <div className="sidebar-header">
        <img
          src="/images/logo/logo-negative.svg"
          alt="All-Voiced logo"
          className="sidebar-logo"
        />
      </div>

      <nav className="sidebar-nav">
        {sidebarItems.map((menu) => {
          if (!canAccess(menu)) return null;

          if (menu.section) {
            const visibleItems =
              menu.items?.filter((item) => canAccess(item)) || [];

            if (visibleItems.length === 0) {
              return null;
            }

            return (
              <div
                key={menu.section}
                className="sidebar-section"
              >
                <div className="sidebar-section-label">
                  <AVIcon
                    icon={menu.icon || "isotypePositive"}
                    size={16}
                    className="sidebar-section-label__icon"
                  />

                  <span>{t(menu.section)}</span>
                </div>

                <div className="sidebar-section-items">
                  {visibleItems.map((item) =>
                    renderNavButton(item)
                  )}
                </div>
              </div>
            );
          }

          return renderNavButton(menu, {
            isPrimary: menu.id === "dashboard"
          });
        })}
      </nav>

      <div className="sidebar-footer">
        {user?.role === "admin" && (
          <div className="sidebar-footer__role-switcher">
            <label
              htmlFor="viewAs"
              className="viewas-label"
            >
              View as:
            </label>

            <select
              id="viewAs"
              value={effectiveRole}
              onChange={(event) => setEffectiveRole(event.target.value)}
              className="viewas-select"
            >
              {GLOBAL_USER_ROLE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          type="button"
          className="sidebar-btn sidebar-btn--utility"
          aria-haspopup="dialog"
          onClick={() => {
            onOpenHelpSupport?.();
            setSidebarOpen?.(false);
          }}
        >
          <AVIcon
            icon="help"
            size={22}
            className="sidebar-icon"
            aria-hidden="true"
          />
          <span>{t('help_and_support', { defaultValue: 'Help & support' })}</span>
        </button>
      </div>
    </aside>
  );
}
